import {
    ZodArray,
    ZodDiscriminatedUnion,
    ZodNullable,
    ZodObject,
    ZodOptional,
    ZodUnion,
    type ZodType,
} from 'zod';
import {reportError} from '@deskly/observability';
import {debug} from '../../shared/helpers/debug';
import {useToast} from '../../shared/components/toast/useToast';

type SchemaIssue = {
    readonly path: readonly PropertyKey[];
    readonly code: string;
};

const REPORTED_ISSUE_LIMIT = 5;
const ROOT_PATH = '<root>';
const ARRAY_INDEX = '[]';

export function parseZodSchema<TOutput, TData>(
    // The type constraint prevents accidentally passing a zod schema as data parameter
    data: TData extends ZodType<TOutput> ? never : unknown,
    schema: ZodType<TOutput>
): TOutput {
    const result = schema.safeParse(data);
    if (!result.success) {
        debug.error(result.error);

        if (import.meta.env.DEV) {
            useToast().error('Error parsing data', {
                detail: result.error.message,
            });
            throw result.error;
        }

        reportSchemaIssues(result.error.issues);

        try {
            // try to parse as many properties as possible
            return recursivelyParse(data, schema as ZodType) as TOutput;
        } catch (e) {
            reportError(e);
            return data as TOutput;
        }
    }

    return result.data as TOutput;
}

class SchemaParseError extends Error {
    constructor(summary: string) {
        super(`Schema parse failed at ${summary}`);
        this.name = 'SchemaParseError';
    }
}

// A ZodError carries the rejected input in its message, which is both a PII leak and a title Sentry
// cannot group on. Only the issue paths and codes are reported, and they also form the fingerprint.
function reportSchemaIssues(issues: readonly SchemaIssue[]): void {
    const distinctIssues = collectDistinctIssues(issues);

    reportError(
        new SchemaParseError(summarizeIssues(distinctIssues)),
        {issueCount: issues.length},
        {fingerprint: ['schema-parse', ...distinctIssues]}
    );
}

// Deduplicated and sorted so the fingerprint ignores row count and row order.
function collectDistinctIssues(issues: readonly SchemaIssue[]): string[] {
    const formatted = issues.map(issue => `${formatIssuePath(issue.path)} (${issue.code})`);

    return [...new Set(formatted)].sort();
}

function summarizeIssues(distinctIssues: readonly string[]): string {
    const summarized = distinctIssues.slice(0, REPORTED_ISSUE_LIMIT);

    if (distinctIssues.length > REPORTED_ISSUE_LIMIT) {
        summarized.push(`and ${distinctIssues.length - REPORTED_ISSUE_LIMIT} more`);
    }

    return summarized.join(', ');
}

// Array indices collapse to [], so the same broken field across rows stays one issue.
function formatIssuePath(path: readonly PropertyKey[]): string {
    return (
        path.reduce<string>((formatted, segment) => {
            if (typeof segment === 'number') {
                return `${formatted}${ARRAY_INDEX}`;
            }

            return formatted ? `${formatted}.${String(segment)}` : String(segment);
        }, '') || ROOT_PATH
    );
}

function recursivelyParse(data: unknown, schema: ZodType): unknown {
    if (schema instanceof ZodObject && typeof data === 'object' && data !== null) {
        const shape = schema.shape;
        const result: Record<string, unknown> = {};

        for (const key in shape) {
            const subSchema = shape[key];
            try {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const parsed = recursivelyParse((data as any)[key], subSchema);
                if (parsed !== undefined) {
                    result[key] = parsed;
                }
            } catch {
                // Ignore error
            }
        }

        return result;
    }

    if (schema instanceof ZodArray && Array.isArray(data)) {
        const elementSchema = schema.element;
        return data
            .map(item => {
                try {
                    return recursivelyParse(item, elementSchema as ZodType);
                } catch {
                    return undefined;
                }
            })
            .filter(item => item !== undefined);
    }

    if (schema instanceof ZodOptional || schema instanceof ZodNullable) {
        if (data === undefined || data === null) return data;
        return recursivelyParse(data, schema.unwrap() as ZodType);
    }

    if (schema instanceof ZodUnion || schema instanceof ZodDiscriminatedUnion) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        for (const option of 'options' in schema ? schema.options : (schema as any)._def.options) {
            const result = option.safeParse(data);
            if (result.success) return result.data;
        }
        throw new Error('Union schema: all options failed');
    }

    const result = schema.safeParse(data);
    if (result.success) {
        return result.data;
    }

    return data;
}
